export interface Crumb {
  label: string;
  path?: string;
  current?: boolean;
}

function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

function decodeSegment(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

// React Router matches static path segments case-insensitively and after
// percent-decoding, so `/cases/acme/COLLECTION` and `/cases/acme/%63ollection`
// mount the same screen as the canonical spelling. Recognising only the literal
// form left those URLs with a rendered screen but no trail — and, once the Header
// chevron was retired, no way out. Read the route the way the router reads it.
// Returns the suffix under `/cases/<caseKey>`, or null when the pathname is not a
// case-scoped route (the bare `/cases` index included).
// Two spellings of the same route compare equal under this key: every segment
// decoded, then lowercased. Use it to ask "is this the same route?", never to build
// an href — hrefs must keep the ID spelling the caller gave us.
export function routeIdentity(pathname: string): string {
  return normalizePath(pathname).split('/').map(decodeSegment).join('/').toLowerCase();
}

export function caseRouteSuffix(pathname: string, caseKey: string | null): string | null {
  const raw = normalizePath(pathname).split('/');
  const segments = raw.map(decodeSegment);
  if (raw.length < 3 || raw[0] !== '' || segments[1].toLowerCase() !== 'cases') return null;
  // Callers supply the key either decoded (buildCaseKey returns the raw case_ref)
  // or in its URL spelling; accept both rather than forcing one convention.
  if (!caseKey || (segments[2] !== caseKey && raw[2] !== caseKey)) return null;
  return raw.length > 3 ? `/${segments.slice(3).join('/')}` : '';
}

export function crumbsFromPath(pathname: string, caseKey: string | null, caseName: string | null): Crumb[] {
  const raw = normalizePath(pathname).split('/');
  const segments = raw.map(decodeSegment);
  if (raw.length < 2 || raw[0] !== '' || segments[1].toLowerCase() !== 'cases') return [];
  const root: Crumb[] = [{ label: 'Cases', path: '/cases' }];
  if (raw.length === 2) return root;
  // Public resolver guard: independent callers may supply a key from another route.
  if (!caseKey || (segments[2] !== caseKey && raw[2] !== caseKey)) return root;
  const C = `/cases/${caseKey}`;
  const base: Crumb[] = [...root, { label: caseName?.trim() ? caseName : caseKey, path: C }];
  // IDs keep the caller's spelling; only static route words are canonicalised.
  const key = raw.length > 3 ? `/${segments.slice(3).join('/')}`.toLowerCase() : '';
  const path = `${C}${key}`;
  const labels: Record<string, string> = {
    '/documents/review': 'Document Discovery',
    '/members': 'Case Access',
    '/chronology': 'Chronology',
    '/claims-matrix': 'Claims Matrix',
    '/ai/graph-search': 'Ask AI',
  };
  if (Object.prototype.hasOwnProperty.call(labels, key)) {
    return [...base, { label: labels[key], path }];
  }
  const D = `${C}/discovery/responses`;
  const responseTrail: Crumb[] = [...base, { label: 'Discovery Responses', path: D }];
  if (key === '/discovery/responses') return responseTrail;
  const responseLabels: Record<string, string> = {
    '/discovery/responses/all': 'Review All Responses',
    '/discovery/responses/files': 'All Files',
    '/discovery/responses/uploads': 'Uploads',
    '/discovery/responses/intake': 'Intake',
    '/discovery/responses/compare': 'Cross-Party Comparison',
    '/discovery/responses/history': 'Version History',
  };
  if (Object.prototype.hasOwnProperty.call(responseLabels, key)) {
    return [...responseTrail, { label: responseLabels[key], path }];
  }
  // Segment-wise so an encoded static word (`%69ntake`) is recognised too; the ID
  // comes from `rest`, which is untouched.
  const rest = raw.slice(3);
  const words = segments.slice(3).map(segment => segment.toLowerCase());
  const isResponseChild = words[0] === 'discovery' && words[1] === 'responses';
  if (isResponseChild && words[2] === 'intake' && rest[3] && (words.length === 4 || (words.length === 5 && words[4] === 'review'))) {
    const trail: Crumb[] = [...responseTrail, { label: 'Intake', path: `${D}/intake/${rest[3]}` }];
    return words.length === 5 ? [...trail, { label: 'Review', path: `${D}/intake/${rest[3]}/review` }] : trail;
  }
  if (isResponseChild && words[2] === 'batch' && rest[3] && words.length === 4) {
    return [...responseTrail, { label: 'Batch', path: `${D}/batch/${rest[3]}` }];
  }
  return base;
}
